import { useEffect, useRef, useState } from 'react';
import { api } from '../../api';
import { formatMoney } from '../../utils/currency';
import { PayurusReturnPanel } from './ActivePayurusPanel';

const STATUS_POLL_MS = 4000;

function formatLeft(seconds) {
  const s = Math.max(0, Number(seconds) || 0);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
}

function outcomeFromStatus(status, callbackStatus) {
  const s = String(status || '').toLowerCase();
  const cb = String(callbackStatus || '').toUpperCase();
  if (s === 'completed' || s === 'approved' || cb === 'APPROVED') return 'completed';
  if (
    s === 'cancelled'
    || s === 'canceled'
    || s === 'rejected'
    || s === 'failed'
    || s === 'declined'
    || ['DECLINED', 'REJECTED', 'CANCELED', 'CANCELLED'].includes(cb)
  ) {
    return 'failed';
  }
  if (s === 'expired') return 'expired';
  return null;
}

function labelWithColon(text) {
  const value = String(text || '').trim();
  if (!value) return '';
  return value.endsWith(':') ? value : `${value}:`;
}

function CopyButton({ value, label, copiedLabel }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="payments-form__quick-btn payments-havale-active__copy-btn"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(String(value || ''));
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch {
        }
      }}
    >
      {copied ? copiedLabel : label}
    </button>
  );
}

export function openMaxHavalePaymentUrl(url) {
  const href = String(url || '').trim();
  if (!href || typeof window === 'undefined') return false;
  try {
    const opened = window.open(href, '_blank', 'noopener,noreferrer');
    if (opened) return true;
  } catch {
  }
  window.location.assign(href);
  return true;
}

export function ActiveMaxHavalePanel({
  request,
  tr: translateProp,
  t: translateLegacy,
  variant = 'desktop',
  token,
  onGoRequests,
  onCancel,
  cancelling = false,
  onOpenPayment,
  onOutcome,
  onContinue,
}) {
  const tr = typeof translateProp === 'function'
    ? translateProp
    : (typeof translateLegacy === 'function' ? translateLegacy : (key) => key);
  const [left, setLeft] = useState(request?.remaining_seconds ?? 0);
  const [liveRequest, setLiveRequest] = useState(request);
  const [phase, setPhase] = useState(() => outcomeFromStatus(
    request?.status,
    request?.callback_status || request?.payment_details?.callback_status,
  ));
  const checkingRef = useRef(false);
  const outcomeNotified = useRef(false);
  const root = variant === 'mobile' ? 'mobile-account-cashier__havale-active' : 'payments-havale-active';
  const title = variant === 'mobile' ? 'mobile-account-cashier__havale-title' : 'payments-havale-active__title';
  const row = variant === 'mobile' ? 'mobile-account-cashier__havale-row' : 'payments-havale-active__row';
  const timer = variant === 'mobile' ? 'mobile-account-cashier__havale-timer' : 'payments-havale-active__timer';
  const spinnerWrap = variant === 'mobile' ? 'mobile-account-cashier__havale-spinner-wrap' : 'payments-havale-active__spinner-wrap';
  const spinner = variant === 'mobile' ? 'mobile-account-cashier__havale-spinner' : 'payments-havale-active__spinner';
  const primaryBtn = variant === 'mobile' ? 'mobile-account-cashier__submit' : 'payments-form__submit';
  const secondaryBtn = variant === 'mobile' ? 'mobile-account-cashier__notice-btn' : 'payments-form__quick-btn';

  useEffect(() => {
    setLiveRequest(request);
    setLeft(request?.remaining_seconds ?? 0);
    const nextPhase = outcomeFromStatus(
      request?.status,
      request?.callback_status || request?.payment_details?.callback_status,
    );
    setPhase(nextPhase);
    outcomeNotified.current = Boolean(nextPhase);
  }, [
    request?.id,
    request?.remaining_seconds,
    request?.status,
    request?.callback_status,
    request?.payment_details?.callback_status,
    request?.iban,
    request?.account_name,
    request?.bank_name,
    request?.redirect_url,
  ]);

  useEffect(() => {
    if (!request?.id || phase || request?.type === 'withdraw') return undefined;
    const id = setInterval(() => setLeft((v) => Math.max(0, v - 1)), 1000);
    return () => clearInterval(id);
  }, [request?.id, phase, request?.type]);

  const applyStatus = (next) => {
    if (!next) return;
    setLiveRequest((prev) => ({
      ...(prev || {}),
      ...next,
      iban: next.iban || prev?.iban || null,
      account_name: next.account_name || prev?.account_name || null,
      bank_name: next.bank_name || prev?.bank_name || null,
      redirect_url: next.redirect_url || prev?.redirect_url || null,
      callback_status: next.callback_status
        || next.payment_details?.callback_status
        || prev?.callback_status
        || prev?.payment_details?.callback_status
        || null,
    }));
    if (typeof next.remaining_seconds === 'number') setLeft(next.remaining_seconds);
    const nextPhase = outcomeFromStatus(
      next.status,
      next.callback_status || next.payment_details?.callback_status,
    );
    if (!nextPhase) return;
    setPhase(nextPhase);
    if (!outcomeNotified.current) {
      outcomeNotified.current = true;
      onOutcome?.(nextPhase, next);
    }
  };

  const checkStatus = async () => {
    if (!token || !request?.id || checkingRef.current || phase) return;
    checkingRef.current = true;
    try {
      const data = await api.cashierRequest(token, request.id);
      applyStatus(data.request || null);
    } catch {
    } finally {
      checkingRef.current = false;
    }
  };

  useEffect(() => {
    if (!token || !request?.id || phase || request?.type === 'withdraw') return undefined;
    checkStatus();
    const id = window.setInterval(checkStatus, STATUS_POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') checkStatus();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [token, request?.id, phase, request?.type]);

  useEffect(() => {
    if (phase || left > 0 || request?.type === 'withdraw') return;
    checkStatus();
  }, [left, phase, request?.type]);

  if (!liveRequest && !request) return null;

  const current = liveRequest || request || {};
  const safeText = (v) => (v == null || (typeof v === 'object') ? '' : String(v));

  if (phase) {
    return (
      <PayurusReturnPanel
        phase={phase}
        request={current}
        tr={tr}
        variant={variant}
        provider="maxhavale"
        onContinue={() => {
          onContinue?.();
        }}
        onGoRequests={onGoRequests}
      />
    );
  }

  if (current.type === 'withdraw') {
    return (
      <div className={root}>
        <h3 className={title}>{tr('account.payments.maxhavale.withdrawPending')}</h3>
        <div className={row}>
          <span>{labelWithColon(tr('account.payments.havale.amountLabel'))}</span>
          <strong>{formatMoney(current.amount)}</strong>
        </div>
        {current.bank_name ? (
          <div className={row}>
            <span>{labelWithColon(tr('account.payments.havale.bank'))}</span>
            <strong>{safeText(current.bank_name)}</strong>
          </div>
        ) : null}
        {current.iban ? (
          <div className={row}>
            <span>{labelWithColon(tr('account.payments.havale.iban'))}</span>
            <strong>{safeText(current.iban)}</strong>
          </div>
        ) : null}
        {current.account_name ? (
          <div className={row}>
            <span>{labelWithColon(tr('account.payments.havalespeed.accountHolder'))}</span>
            <strong>{safeText(current.account_name)}</strong>
          </div>
        ) : null}
        {onGoRequests ? (
          <button type="button" className={secondaryBtn} onClick={onGoRequests}>
            {tr('account.payments.requests')}
          </button>
        ) : null}
      </div>
    );
  }

  const hasBank = Boolean(current.iban || current.account_name);
  const hasRedirect = Boolean(current.redirect_url);
  const waitingDetails = !hasBank && !hasRedirect;
  const confirmed = Boolean(
    current.confirm_status === 'notified'
    || current.deposit_confirmed
    || current.user_confirmed,
  );

  return (
    <div className={root}>
      <div className={timer}>
        <span>{tr('account.payments.havale.countdown')}</span>
        <strong>{formatLeft(left)}</strong>
      </div>
      <h3 className={title}>{tr('account.payments.maxhavale.depositPending')}</h3>
      {waitingDetails ? (
        <div className={spinnerWrap} aria-busy="true">
          <div className={spinner} aria-hidden="true" />
        </div>
      ) : (
        <>
          {hasBank ? <p style={{ marginTop: 4 }}>{tr('account.payments.havale.transferTo')}</p> : null}
          {hasBank ? (
            <>
              <div className={row}>
                <span>{labelWithColon(tr('account.payments.havale.amountLabel'))}</span>
                <strong>{formatMoney(current.amount)}</strong>
              </div>
              {current.bank_name ? (
                <div className={row}>
                  <span>{labelWithColon(tr('account.payments.havale.bank'))}</span>
                  <strong>{safeText(current.bank_name)}</strong>
                </div>
              ) : null}
              {current.account_name ? (
                <div className={row}>
                  <span>{labelWithColon(tr('account.payments.havalespeed.accountHolder'))}</span>
                  <strong>{safeText(current.account_name)}</strong>
                  <CopyButton
                    value={safeText(current.account_name)}
                    label={tr('account.payments.havale.copyName')}
                    copiedLabel={tr('account.payments.havale.copied')}
                  />
                </div>
              ) : null}
              {current.iban ? (
                <div className={row}>
                  <span>{labelWithColon(tr('account.payments.havale.iban'))}</span>
                  <strong>{safeText(current.iban)}</strong>
                  <CopyButton
                    value={safeText(current.iban)}
                    label={tr('account.payments.havale.copyIban')}
                    copiedLabel={tr('account.payments.havale.copied')}
                  />
                </div>
              ) : null}
            </>
          ) : null}
          {hasRedirect ? (
            <button
              type="button"
              className={primaryBtn}
              style={{ marginTop: 12 }}
              onClick={() => {
                if (onOpenPayment) onOpenPayment(current.redirect_url);
                else openMaxHavalePaymentUrl(current.redirect_url);
              }}
            >
              {tr('account.payments.maxhavale.openPayment')}
            </button>
          ) : null}
        </>
      )}
      {!confirmed && onCancel ? (
        <button
          type="button"
          className={secondaryBtn}
          style={{ marginTop: 10 }}
          disabled={cancelling}
          onClick={onCancel}
        >
          {cancelling
            ? tr('account.payments.maxhavale.cancelling')
            : tr('account.payments.maxhavale.cancelDeposit')}
        </button>
      ) : null}
      {onGoRequests ? (
        <button type="button" className={secondaryBtn} style={{ marginTop: 8 }} onClick={onGoRequests}>
          {tr('account.payments.requests')}
        </button>
      ) : null}
    </div>
  );
}
